/**
 * Admin Client (App) Management API
 *
 * Requires admin session token (X-Session-Token with admin privileges).
 * All calls go to /admin/clients/* endpoints.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * List all client registrations (apps) in the organisation.
 * @param {string} [statusFilter] - optional: "active" | "revoked"
 * @returns {Promise<Array>} Array of client objects
 */
export const listAdminClients = async (statusFilter) => {
  try {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_CLIENTS,
      params,
    });
    return response.data.clients;
  } catch (error) {
    logger.error("adminClients", "Error listing clients", { error: error.message });
    throw error;
  }
};

/**
 * Register a new client integration (app).
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.description
 * @param {string} payload.client_type
 * @param {string} payload.client_auth_method
 * @param {Object} payload.policy_prism
 * @param {string} [payload.owner_team]
 * @param {string} [payload.owner_contact]
 * @param {string} [payload.docs_url]
 * @param {boolean} [payload.sandbox]
 * @returns {Promise<{client_id: string, client_secret: string}>}
 */
export const registerAdminClient = async (payload) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_REGISTER_CLIENT,
      data: payload,
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error registering client", { error: error.message });
    throw error;
  }
};

/**
 * Get a single client registration.
 * @param {string} clientId
 * @returns {Promise<Object>} Client document
 */
export const getAdminClient = async (clientId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_CLIENT(clientId),
    });
    return response.data.client;
  } catch (error) {
    logger.error("adminClients", "Error fetching client", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Update mutable fields on a client registration.
 * @param {string} clientId
 * @param {Object} updates - fields to update
 * @returns {Promise<{message: string}>}
 */
export const updateAdminClient = async (clientId, updates) => {
  try {
    const response = await api({
      method: "PATCH",
      url: ENDPOINTS.ADMIN_UPDATE_CLIENT(clientId),
      data: updates,
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error updating client", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Permanently delete a client registration and all its data.
 * @param {string} clientId
 * @returns {Promise<{message: string}>}
 */
export const deleteAdminClient = async (clientId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_DELETE_CLIENT(clientId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error deleting client", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Re-enable a previously revoked client.
 * @param {string} clientId
 * @returns {Promise<{message: string}>}
 */
export const enableAdminClient = async (clientId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_ENABLE_CLIENT(clientId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error enabling client", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Permanently revoke a client integration.
 * @param {string} clientId
 * @returns {Promise<{message: string}>}
 */
export const revokeAdminClient = async (clientId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_REVOKE_CLIENT(clientId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error revoking client", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Rotate the CLIENT_ATOM and client_secret for a registered client.
 * Returns the new plain-text secret (shown once — not stored).
 * @param {string} clientId
 * @returns {Promise<{client_secret: string, client_atom_birthmark: string}>}
 */
export const rotateAdminClient = async (clientId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_ROTATE_CLIENT(clientId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error rotating client credentials", { error: error.message, clientId });
    throw error;
  }
};

/**
 * List all subject links for a client registration.
 * @param {string} clientId
 * @returns {Promise<Array>} Array of subject link objects
 */
export const listAdminClientSubjects = async (clientId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_CLIENT_SUBJECTS(clientId),
    });
    return response.data.subjects;
  } catch (error) {
    logger.error("adminClients", "Error listing client subjects", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Tombstone a subject link, preventing future access.
 * @param {string} clientId
 * @param {string} linkId
 * @returns {Promise<Object>}
 */
export const tombstoneAdminClientSubject = async (clientId, linkId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_TOMBSTONE_SUBJECT(clientId, linkId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error tombstoning subject link", { error: error.message, clientId, linkId });
    throw error;
  }
};

/**
 * List all subject invites for a client registration.
 * @param {string} clientId
 * @returns {Promise<Array>} Array of invite objects
 */
export const listAdminClientInvites = async (clientId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_CLIENT_INVITES(clientId),
    });
    return response.data.invites;
  } catch (error) {
    logger.error("adminClients", "Error listing client invites", { error: error.message, clientId });
    throw error;
  }
};

/**
 * Create a subject invite for a client (requires allow_external_subjects=true).
 * @param {string} clientId
 * @param {string} email
 * @returns {Promise<{invite_id: string, email: string, status: string}>}
 */
export const createAdminClientInvite = async (clientId, email) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_CREATE_CLIENT_INVITE(clientId),
      data: { email },
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error creating client invite", { error: error.message, clientId, email });
    throw error;
  }
};

/**
 * Decommission a device subject: tombstone link, stamp call_records, optional purge.
 * @param {string} clientId
 * @param {string} deviceId - the client_subject_id of the device
 * @param {{ reason: string, purge_content?: boolean }} body
 * @returns {Promise<{status: string, device_id: string, call_records_stamped: number, purge_triggered: boolean}>}
 */
export const decommissionAdminClientDevice = async (clientId, deviceId, body) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_DECOMMISSION_DEVICE(clientId, deviceId),
      data: body,
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error decommissioning device", { error: error.message, clientId, deviceId });
    throw error;
  }
};

/**
 * Revoke (delete) a subject invite.
 * @param {string} clientId
 * @param {string} inviteId
 * @returns {Promise<Object>}
 */
export const deleteAdminClientInvite = async (clientId, inviteId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_DELETE_CLIENT_INVITE(clientId, inviteId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminClients", "Error revoking client invite", { error: error.message, clientId, inviteId });
    throw error;
  }
};
